<template>
    <div>
        <!-- Same tabs as the Scorecard (shared ?team / ?mine): My rocks (every rock you own, on any team), then
             one per team you can open (Team Hub Admins see every team; everyone else just their own) -->
        <div v-if="loaded && !noTeam && !team" class="sb-teams" role="tablist">
            <button v-if="myEmployeeID" type="button" role="tab" :aria-selected="!searching && showMine" class="sb-team" :class="!searching && showMine ? 'on' : ''" @click="selectMine()">
                <span class="sb-team-name"><svg-icon icon="user" class="sb-team-ic" />My rocks</span>
                <span class="sb-team-meta">
                    {{ myRocks.length }} across {{ myTeamCount }} team{{ myTeamCount == 1 ? '' : 's' }}<template v-if="tabStats(myRocks).off"> · <b class="bad">{{ tabStats(myRocks).off }} off track</b></template>
                </span>
            </button>
            <button v-for="t in teams" :key="t" type="button" role="tab" :aria-selected="!searching && !showMine && t == activeTeam" class="sb-team" :class="!searching && !showMine && t == activeTeam ? 'on' : ''" @click="selectTeam(t)">
                <span class="sb-team-name">{{ teamLabel(t) }}</span>
                <span class="sb-team-meta">
                    {{ teamRocks(t).length }} rock{{ teamRocks(t).length == 1 ? '' : 's' }}<template v-if="tabStats(teamRocks(t)).off"> · <b class="bad">{{ tabStats(teamRocks(t)).off }} off track</b></template>
                </span>
            </button>
        </div>

        <div v-if="!noTeam" class="rk-bar">
            <div v-if="!team" class="rk-seg">
                <button v-for="q in quarters" :key="q" type="button" :class="q == quarter ? 'on' : ''" @click="selectQuarter(q)">
                    {{ quarterLabel(q) }}<template v-if="q == current"> · now</template><template v-else-if="q == nextQ"> · next</template>
                </button>
            </div>
            <!-- Searches every rock you can see this quarter, on every team (a tab click clears it) -->
            <div v-if="!team" class="sb-search">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                <input v-model="search" type="text" placeholder="Search all rocks" aria-label="Search all rocks" @keyup.esc="search = ''" />
                <span v-if="searching" class="sb-search-count">{{ visibleRocks.length }} found</span>
                <button v-if="search" type="button" class="sb-search-clear" title="Clear search" aria-label="Clear search" @click="search = ''"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
            </div>
            <span style="flex:1"></span>
            <!-- The Forge, set to Rocks: improve the ones here, add milestones or draft new ones (ForgeTeam) -->
            <div v-if="canAdd && !team" class="ai-btn rk-btn rk-forge" @click="showForge = true"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>Improve with AI</div>
            <div v-if="(quarter == current || quarter == nextQ) && myEmployeeID && !team" class="btn-light rk-btn" @click="showRockWeek = true">Plan {{ quarterLabel(nextQ) }}</div>
            <div v-if="canAdd" class="btn rk-btn" @click="openRock(null)">Add Rock</div>
        </div>

        <!-- Quarter summary, for the selected tab -->
        <div v-if="loaded && !noTeam && (!team || activeTeam)" class="rk-sum">
            <template v-if="quarter == current">
                <div class="rk-sum-main">
                    <div class="rk-sum-k">{{ tabLabel }} · {{ quarterLabel(quarter) }} · week {{ weekNow }} of 13</div>
                    <div class="rk-sum-v">{{ daysLeft(quarter) }} days left · ends {{ shortDate(bounds.end) }}</div>
                    <div class="rk-qbar"><span :style="'width:' + Math.round(weekNow / 13 * 100) + '%'"></span></div>
                </div>
                <div class="rk-stat ok"><b>{{ countStatus('on') }}</b><span>on track</span></div>
                <div class="rk-stat bad"><b>{{ countStatus('off') }}</b><span>off track</span></div>
                <div class="rk-stat"><b>{{ countStatus('done') }}</b><span>done</span></div>
                <div class="rk-sum-msg">
                    <template v-if="lateRocks.length">
                        <span class="bad">{{ lateRocks.length }} rock{{ lateRocks.length > 1 ? 's have' : ' has' }} a missed milestone:</span>
                        {{ lateRocks.slice(0, 2).map(r => r.Name).join(', ') }}{{ lateRocks.length > 2 ? '…' : '' }}
                    </template>
                    <template v-else>No missed milestones. Owners say on or off track every week.</template>
                </div>
            </template>
            <template v-else-if="quarter > current">
                <div class="rk-sum-main">
                    <div class="rk-sum-k">{{ tabLabel }} · {{ quarterLabel(quarter) }} · planned</div>
                    <div class="rk-sum-v">{{ visibleRocks.length }} rock{{ visibleRocks.length == 1 ? '' : 's' }} set · starts {{ shortDate(bounds.start) }}</div>
                </div>
                <div class="rk-sum-msg">3 to 7 rocks per team. Every rock gets one owner and milestones.</div>
            </template>
            <template v-else>
                <div class="rk-sum-main">
                    <div class="rk-sum-k">{{ tabLabel }} · {{ quarterLabel(quarter) }} · complete</div>
                    <div class="rk-sum-v">{{ countStatus('done') }} of {{ visibleRocks.length }} rocks done · {{ pastPct }}%</div>
                    <div class="rk-qbar" :class="pastPct >= 80 ? 'ok' : ''"><span :style="'width:' + pastPct + '%'"></span></div>
                </div>
                <div class="rk-sum-msg">{{ pastPct >= 80 ? 'Hit the 80% EOS target.' : 'EOS aims for 80% or better.' }}</div>
            </template>
        </div>

        <template v-if="loaded">
            <div v-if="rockGroups.length" class="rk-rocks">
                <template v-for="g in rockGroups" :key="g.key">
                <!-- A header per group: Team Rocks / Individual Rocks on a team's tab, each team on My rocks -->
                <div class="rk-group-h">{{ g.label }}<span>{{ g.rocks.length }}</span></div>
                <div v-for="r in g.rocks" :key="r.ID" class="rk-rock" :class="r.Status" role="button" tabindex="0" :aria-label="r.Name" @click="openRock(r)" @keydown.enter.self="openRock(r)">
                    <div class="rk-rtop">
                        <span class="rk-own" :title="ownerNames(r)">{{ initials(ownerName(r.OwnerEmployeeID)) }}</span>
                        <!-- Name, then its owner (and where it was carried from) underneath, as on a Scorecard card -->
                        <div class="rk-rhead">
                            <div class="rk-rname">{{ r.Name }}</div>
                            <div class="rk-meta">
                                <span :title="ownerNames(r)">{{ ownerName(r.OwnerEmployeeID) }}<template v-if="(r.AdditionalOwnerIDs || []).length">, {{ r.AdditionalOwnerIDs.map(firstName).join(', ') }}</template></span>
                                <!-- On My rocks and search results there's no Team Rocks header to say so -->
                                <span v-if="byTeam && r.TeamRock" class="rk-chip">Team Rock</span>
                                <span v-if="r.CarriedFromRockID" class="rk-chip">From {{ quarterLabel(prevQuarter(r.Quarter)) }}</span>
                                <!-- The rock's other teams (its team and additional teams, less the one being looked at) -->
                                <span v-if="otherTeams(r).length" class="rk-chip" :title="'Also on ' + otherTeams(r).map(teamLabel).join(', ')">Also on {{ otherTeams(r).map(teamLabel).join(', ') }}</span>
                            </div>
                        </div>
                        <button v-if="canChange(r) && live && (r.Status == 'on' || r.Status == 'off')" type="button" class="rk-st" :class="statusInfo(r).cls" title="Click to switch on/off track" @click.stop="toggleStatus(r)">{{ statusInfo(r).label }} ⇄</button>
                        <span v-else class="rk-st" :class="statusInfo(r).cls">{{ statusInfo(r).label }}</span>
                    </div>
                    <!-- What finished looks like (the rock's Done statement), above its milestones -->
                    <div v-if="r.DoneStatement" class="rk-done"><b>Done:</b> {{ r.DoneStatement }}</div>
                    <!-- No milestones yet: just the line below, which says so -->
                    <div v-if="r.Milestones.length" class="rk-ms">
                        <div class="rk-dots">
                            <span v-for="m in r.Milestones" :key="m.ID" class="rk-dot" :class="m.Done ? 'd' : (m.DueDate < todayDate ? 'late' : '')" :title="m.Text + ' · ' + shortDate(m.DueDate) + ' · ' + ownerName(m.OwnerEmployeeID || r.OwnerEmployeeID) + (m.Done ? ' · done' : '')"></span>
                        </div>
                        <span>{{ r.Milestones.filter(m => m.Done).length }}/{{ r.Milestones.length }} milestones</span>
                    </div>
                    <div class="rk-foot">
                        <div class="rk-next" :class="nextLine(r).cls">
                            {{ nextLine(r).text }}
                            <!-- That milestone's link (RockModal), opened without opening the rock -->
                            <a v-if="nextLine(r).link" class="rk-next-link" :href="nextLine(r).link" target="_blank" rel="noopener" :title="nextLine(r).link" @click.stop><svg-icon icon="link" /><span>{{ linkLabel(nextLine(r).link) }}</span></a>
                        </div>
                        <button type="button" class="rk-issue" @click.stop="openIssue(r)">Issue</button>
                    </div>
                </div>
                </template>
            </div>
            <!-- Same as the Scoreboard's empty team -->
            <div v-else class="rk-empty">
                <svg-icon icon="star" />
                <div v-if="searching">No rocks for {{ quarterLabel(quarter) }} match "{{ search.trim() }}".</div>
                <div v-else-if="showMine">You don't own any rocks for {{ quarterLabel(quarter) }}. Rocks you're the owner of, on any team, show up here.</div>
                <div v-else-if="team && !activeTeam">You can't open the {{ teamLabel(team) }} rocks. Only people on the team and Team Hub admins can.</div>
                <div v-else-if="activeTeam">{{ teamLabel(activeTeam) }} doesn't have any rocks for {{ quarterLabel(quarter) }} yet.</div>
                <div v-else>You don't belong to a team yet. Contact your administrator to be added to a team.</div>
                <div v-if="searching" class="btn-light rk-btn" @click="search = ''">Clear Search</div>
                <div v-else-if="canAdd" class="btn rk-btn" @click="openRock(null)">Add the First Rock</div>
            </div>
        </template>

        <rock-modal v-if="showRock" :rock="rockToEdit" :quarter="quarter" :default-team="newRockTeam" :employees="employees" :teams="teams" :myEmployeeID="myEmployeeID" @close="showRock = false" @saved="onRockSaved" />
        <forge-team v-if="showForge" modal :only="[ 'improve', 'milestones', 'rocks' ]" start-kind="improve" :preset-team="newRockTeam || ''" :preset-quarter="quarter" @applied="load()" @close="showForge = false" />
        <!-- A rock's Issue: a card on a meeting board (the running meeting's, when in one) -->
        <quick-add-agenda-item v-if="issue" heading="Add Issue" prefer-column="Issues" :preset="issue.preset" :team="issue.team" :fixed-meeting="meeting" @close="issue = null" />
        <rock-week-modal v-if="showRockWeek" :quarter="current" :employees="employees" :teams="teams" :default-team="newRockTeam" :myEmployeeID="myEmployeeID" @close="closeRockWeek()" @changed="load()" @finish="finishRockWeek" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import RockModal from './RockModal.vue';
import RockWeekModal from './RockWeekModal.vue';
import ForgeTeam from './ForgeTeam.vue';
import SvgIcon from './SvgIcon.vue';
import QuickAddAgendaItem from './QuickAddAgendaItem.vue';
import { initials, teamLabel, COMPANY_TEAM } from '@/scoreboard';
import {
    today, currentQuarter, quarterLabel, quarterBounds, nextQuarter, prevQuarter, weekOfQuarter, daysLeft, shortDate, lateMilestones, rockStatus, rockOwnerIDs, isRockOwner, rockTeamsOf,
    rockIssue, milestoneFileName
} from '@/rocks';

export default defineComponent({
    name: 'RocksView',
    props: {
        // Show just this team's rocks for the current quarter (no tabs, no quarter picker, no ?team / ?quarter in the
        // link): a running meeting's section (Meetings.vue)
        team: { type: String, default: '' },
        // The meeting being run (Meetings.vue): Issues go on its board
        meeting: { type: Object, default: null }
    },
    components: {
        QuickAddAgendaItem,
        RockModal,
        RockWeekModal,
        ForgeTeam,
        SvgIcon
    },
    data() {
        const current = currentQuarter();
        return {
            current,
            nextQ: nextQuarter(current),
            todayDate: today(),
            quarter: current,
            loaded: false,
            rocks: [] as any[],
            employees: [] as any[],
            teams: [] as string[], // the teams you can open (the tabs)
            myEmployeeID: null as any,
            myTeam: null as any,
            selectedTeam: '',
            showMine: false, // the My rocks tab (?mine=1, shared with the Scorecard)
            search: '',
            showRock: false,
            rockToEdit: null as any,
            showRockWeek: false,
            showForge: false,
            issue: null as any // { preset, team }: the Add Issue form
        }
    },
    async created() {
        if(this.team) {
            await this.load();
            return;
        }
        const q = String(this.$route.query.quarter || '');
        if(/^\d{4}-Q[1-4]$/.test(q)) {
            this.quarter = q;
        }
        this.selectedTeam = String(this.$route.query.team || '');
        this.showMine = this.$route.query.mine == '1';
        await this.load();
        // With nothing picked in the link, start on My rocks when you own any (as the Scorecard does)
        if(!this.$route.query.team && !this.$route.query.mine && this.myRocks.length) {
            this.showMine = true;
        }
        if(!this.myEmployeeID) {
            this.showMine = false;
        }
        // ?rock=<id> opens that rock (a task manager card's milestone badge, CompanyBoard.vue)
        const linked = this.rocks.find((r: any) => r.ID == this.$route.query.rock);
        if(linked) {
            this.rockToEdit = linked;
            this.showRock = true;
        }
    },
    computed: {
        // Last quarter, this one, and next (always, for planning ahead)
        quarters(): string[] {
            const list = [ prevQuarter(this.current), this.current, this.nextQ ];
            if(!list.includes(this.quarter)) {
                list.push(this.quarter);
            }
            return list.sort();
        },
        bounds(): { start: string, end: string } {
            return quarterBounds(this.quarter);
        },
        // Rocks can still change status in the current and upcoming quarters
        live(): boolean {
            return this.quarter >= this.current;
        },
        weekNow(): number {
            return weekOfQuarter(this.quarter, this.todayDate);
        },
        // The fixed team (null when you can't open it); else the selected team, else your own, else the first one you can open
        activeTeam(): string | null {
            if(this.team) {
                return this.teams.includes(this.team) ? this.team : null;
            }
            return this.teams.find((t: string) => t == this.selectedTeam)
                || this.teams.find((t: string) => t == this.myTeam)
                || this.teams[0] || null;
        },
        // A search: just on the page (not in a meeting), and over every team's rocks for the quarter
        searching(): boolean {
            return !this.team && !!this.search.trim();
        },
        // Grouped by each rock's own team: My rocks and search results
        byTeam(): boolean {
            return this.showMine || this.searching;
        },
        tabLabel(): string {
            if(this.searching) {
                return 'Search results';
            }
            return this.showMine ? 'My rocks' : teamLabel(this.activeTeam || '');
        },
        myRocks(): any[] {
            return this.myEmployeeID ? this.rocks.filter((r: any) => isRockOwner(r, this.myEmployeeID)) : [];
        },
        myTeamCount(): number {
            return new Set(this.myRocks.map((r: any) => r.Team)).size;
        },
        // The tab's rocks, or what a search matches (every word, in the name, Done statement, milestones, owners or teams)
        visibleRocks(): any[] {
            if(this.searching) {
                const words = this.search.trim().toLowerCase().split(/\s+/);
                return this.rocks.filter((r: any) => {
                    const text = [
                        r.Name, r.DoneStatement, this.ownerNames(r), ...rockTeamsOf(r).map(teamLabel), ...(r.Milestones || []).map((m: any) => m.Text)
                    ].join(' ').toLowerCase();
                    return words.every((w: string) => text.includes(w));
                });
            }
            return this.showMine ? this.myRocks : this.teamRocks(this.activeTeam);
        },
        // The cards, in groups with a header each. A team's tab: Team Rocks (the whole team's) first, then Individual Rocks.
        // My rocks (as the Scorecard's My numbers) and search results: one per team in the tab order (Company first), then teams you can't
        // open, then any rock with no team, each with its team rocks first.
        rockGroups(): { key: string, label: string, rocks: any[] }[] {
            const teamFirst = (rocks: any[]) => [ ...rocks.filter((r: any) => r.TeamRock), ...rocks.filter((r: any) => !r.TeamRock) ];
            if(!this.byTeam) {
                const groups = [
                    // The team's Team Rocks, headed with its name ("Executive Rocks"), like the other teams' groups under Company
                    { key: 'team', label: this.activeTeam ? teamLabel(this.activeTeam) + ' Rocks' : 'Team Rocks', rocks: this.visibleRocks.filter((r: any) => r.TeamRock) },
                    { key: 'individual', label: 'Individual Rocks', rocks: this.visibleRocks.filter((r: any) => !r.TeamRock) }
                ];
                // Company also shows every other team's Team Rocks, one group per team (in tab order), under its own
                if(this.activeTeam == COMPANY_TEAM) {
                    const others = this.rocks.filter((r: any) => r.TeamRock && r.Team && r.Team != COMPANY_TEAM);
                    const names = [ ...new Set(others.map((r: any) => r.Team)) ] as string[];
                    const rank = (t: string) => this.teams.indexOf(t) >= 0 ? this.teams.indexOf(t) : this.teams.length;
                    names.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
                    for(const team of names) {
                        groups.push({ key: 'other-' + team, label: teamLabel(team) + ' Rocks', rocks: others.filter((r: any) => r.Team == team) });
                    }
                }
                return groups.filter(g => g.rocks.length);
            }
            const names = [ ...new Set(this.visibleRocks.map((r: any) => r.Team || '')) ] as string[];
            const rank = (t: string) => t ? (this.teams.indexOf(t) >= 0 ? this.teams.indexOf(t) : this.teams.length) : this.teams.length + 1;
            names.sort((a, b) => rank(a) - rank(b) || teamLabel(a).localeCompare(teamLabel(b)));
            return names.map(team => ({
                key: 'g-' + team,
                label: team ? teamLabel(team) : 'No team',
                rocks: teamFirst(this.visibleRocks.filter((r: any) => (r.Team || '') == team))
            }));
        },
        // New rocks go on the team being looked at, or your own team from My rocks
        newRockTeam(): string | null {
            return this.showMine ? (this.myTeam && this.teams.includes(this.myTeam) ? this.myTeam : this.teams[0] || null) : this.activeTeam;
        },
        canAdd(): boolean {
            return this.team ? !!this.activeTeam : this.teams.length > 0;
        },
        // No team to open and no rocks of your own: just the "not on a team" message (as on the Scorecard)
        noTeam(): boolean {
            return !this.team && !this.teams.length && !this.myRocks.length;
        },
        lateRocks(): any[] {
            return this.visibleRocks.filter((r: any) => r.Status != 'done' && lateMilestones(r).length);
        },
        pastPct(): number {
            return this.visibleRocks.length ? Math.round(this.countStatus('done') / this.visibleRocks.length * 100) : 0;
        }
    },
    methods: {
        quarterLabel,
        prevQuarter,
        daysLeft,
        shortDate,
        rockTeamsOf,
        // A rock's teams besides the one it's listed under: the tab's team, or its own team where it's grouped by that
        // (My rocks, and other teams' Team Rocks on Company)
        otherTeams(r: any): string[] {
            const here = this.byTeam || !rockTeamsOf(r).includes(this.activeTeam || '') ? r.Team : this.activeTeam;
            return rockTeamsOf(r).filter((t: string) => t != here);
        },
        initials,
        async load() {
            await axios.get(`${this.$store.state.apiUrl}/api/rocks`, { params: { quarter: this.quarter } })
                .then(resp => {
                    this.rocks = resp.data.Rocks;
                    this.employees = resp.data.Employees.map((e: any) => ({ ...e, ID: Number(e.ID) }));
                    this.teams = resp.data.Teams;
                    this.myEmployeeID = resp.data.MyEmployeeID;
                    this.myTeam = resp.data.MyTeam;
                })
                .catch(e => {
                    console.log(e);
                })
            this.loaded = true;
        },
        // Team Hub Admins change any rock, everyone else only their own (rocks.ts canUpdate; checked here too)
        canChange(r: any): boolean {
            return !!r.CanUpdate && (this.$store.state.user.teamHubRole == 'Admin' || isRockOwner(r, this.myEmployeeID));
        },
        teamRocks(team: string | null): any[] {
            return team ? this.rocks.filter((r: any) => rockTeamsOf(r).includes(team)) : [];
        },
        tabStats(rocks: any[]): { off: number } {
            return { off: rocks.filter((r: any) => r.Status == 'off').length };
        },
        selectQuarter(q: string) {
            this.quarter = q;
            this.$router.replace({ query: { ...this.$route.query, quarter: q } });
            this.load();
        },
        selectTeam(team: string) {
            this.selectedTeam = team;
            this.showMine = false;
            this.search = '';
            this.$router.replace({ query: { ...this.$route.query, team, mine: undefined } });
        },
        selectMine() {
            this.showMine = true;
            this.search = '';
            this.$router.replace({ query: { ...this.$route.query, team: undefined, mine: '1' } });
        },
        countStatus(status: string): number {
            return this.visibleRocks.filter((r: any) => r.Status == status).length;
        },
        ownerName(id: any): string {
            return this.employees.find((e: any) => e.ID == id)?.Name || 'No owner';
        },
        firstName(id: any): string {
            return this.ownerName(id).split(/\s+/)[0];
        },
        // The owner and additional owners, in full
        ownerNames(r: any): string {
            return rockOwnerIDs(r).length ? rockOwnerIDs(r).map(this.ownerName).join(', ') : 'No owner';
        },
        statusInfo(r: any): { label: string, cls: string } {
            return rockStatus(r.Status);
        },
        // The line under the dots: what's missed, what's next, or how it ended. link: the named milestone's link, if any.
        nextLine(r: any): { text: string, cls: string, link?: string } {
            if(r.Status == 'notdone') {
                return { text: 'Not finished' + (r.CarriedToQuarter ? ` · carried to ${quarterLabel(r.CarriedToQuarter)}` : ''), cls: 'late' };
            }
            if(!r.Milestones.length) {
                return r.Status == 'done' ? { text: 'Done', cls: 'ok' } : { text: 'No milestones yet. Add 3 to 7.', cls: 'late' };
            }
            const next = r.Milestones.find((m: any) => !m.Done);
            if(!next) {
                return { text: 'All milestones done', cls: 'ok' };
            }
            const late = lateMilestones(r);
            if(late.length && r.Status != 'done') {
                return { text: `Missed: ${late[0].Text} (${shortDate(late[0].DueDate)}, ${this.firstName(late[0].OwnerEmployeeID || r.OwnerEmployeeID)})`, cls: 'late', link: late[0].Link };
            }
            return { text: `Next: ${next.Text} · ${shortDate(next.DueDate)} · ${this.firstName(next.OwnerEmployeeID || r.OwnerEmployeeID)}`, cls: '', link: next.Link };
        },
        // An uploaded file's name, or a link as its host and path, without the scheme or "www."
        linkLabel(link: string): string {
            return milestoneFileName(link) || link.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
        },
        async toggleStatus(r: any) {
            const status = r.Status == 'on' ? 'off' : 'on';
            r.Status = status;
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock/status`, { ID: r.ID, Status: status })
                .catch(e => {
                    console.log(e);
                    r.Status = status == 'on' ? 'off' : 'on';
                    alert('There was an error');
                })
        },
        openRock(r: any) {
            this.rockToEdit = r;
            this.showRock = true;
        },
        // At High priority, starting on a meeting of the team being looked at (the rock's own team from My rocks)
        openIssue(r: any) {
            const team = this.byTeam || !rockTeamsOf(r).includes(this.activeTeam || '') ? r.Team : this.activeTeam;
            this.issue = { preset: { ...rockIssue(r, teamLabel(r.Team || ''), this.ownerName), Priority: 'high' }, team };
        },
        // keepOpen: a status or milestone change from a rock you can update but not edit (the modal stays open)
        async onRockSaved(keepOpen = false) {
            if(!keepOpen) {
                this.showRock = false;
            }
            await this.load();
        },
        closeRockWeek() {
            this.showRockWeek = false;
            this.load();
        },
        finishRockWeek(next: string) {
            this.showRockWeek = false;
            this.selectQuarter(next);
        }
    }
});
</script>

<style scoped>
.rk-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.rk-btn { margin: 0; padding: 7px 14px; white-space: nowrap; }
.rk-forge { display: inline-flex; align-items: center; gap: 6px; }
.rk-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 2px; }
.rk-seg button { background: none; border: none; font-family: inherit; font-size: 12.5px; color: var(--font-dark-gray); padding: 6px 13px; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.rk-seg button.on { background: var(--primary-color); color: #fff; font-weight: 600; }

/* Quarter summary, same look as the Scoreboard's this-week banner */
.rk-sum { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; background: #fff; border: 1px solid var(--mid-gray); border-left: 4px solid var(--primary-color); border-radius: 12px; padding: 14px 18px; margin-bottom: 14px; }
.rk-sum-main { min-width: 240px; }
.rk-sum-k { font-size: 11.5px; font-weight: 700; color: var(--font-mid-gray); }
.rk-sum-v { font-family: var(--head-font); font-weight: 700; font-size: 17px; margin-top: 2px; }
.rk-qbar { height: 6px; background: var(--mid-gray); border-radius: 4px; margin-top: 8px; overflow: hidden; }
.rk-qbar span { display: block; height: 100%; background: var(--dark-gray); border-radius: 4px; }
.rk-qbar.ok span { background: #2E7D46; }
.rk-stat { display: flex; flex-direction: column; align-items: center; min-width: 56px; }
.rk-stat b { font-family: var(--head-font); font-size: 22px; line-height: 1.1; }
.rk-stat span { font-size: 11.5px; color: var(--font-mid-gray); }
.rk-stat.ok b { color: #2E7D46; }
.rk-stat.bad b { color: var(--primary-color); }
.rk-sum-msg { flex: 1; min-width: 220px; font-size: 13px; line-height: 1.45; }
.rk-sum-msg .bad { color: var(--primary-color); font-weight: 600; }

.rk-rocks { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
/* My rocks: a team's header across the grid (same look as the Scorecard's My numbers) */
.rk-group-h { grid-column: 1 / -1; display: flex; align-items: center; gap: 8px; margin-top: 8px; font-family: var(--head-font); font-weight: 700; font-size: 15px; color: var(--dark-gray); }
.rk-group-h:first-child { margin-top: 0; }
.rk-group-h span { font-family: var(--body-font); font-size: 11px; font-weight: 700; color: var(--font-dark-gray); background: var(--mid-gray); border-radius: 9px; padding: 1px 8px; }
.rk-rock { background: #fff; border: 1px solid var(--mid-gray); border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; gap: 9px; }
.rk-rock.on { border-left: 4px solid #2E7D46; }
.rk-rock.off { border-left: 4px solid var(--primary-color); }
.rk-rock.done { border-left: 4px solid var(--dark-gray); }
.rk-rock.notdone { border-left: 4px solid var(--font-mid-gray); opacity: .85; }
.rk-rtop { display: flex; align-items: flex-start; gap: 10px; }
.rk-own { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #024E73; color: #fff; font-weight: 700; font-size: 10px; flex-shrink: 0; }
.rk-rhead { flex: 1; min-width: 0; }
.rk-rname { font-family: var(--head-font); font-weight: 700; font-size: 14.5px; line-height: 1.35; }
/* The whole card opens the rock; the on/off track pill toggles instead */
.rk-rock { cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.rk-rock:hover { border-top-color: var(--font-mid-gray); border-right-color: var(--font-mid-gray); border-bottom-color: var(--font-mid-gray); box-shadow: 0 2px 8px rgba(44,41,38,.08); }
.rk-st { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 700; border-radius: 12px; padding: 3px 9px; border: none; white-space: nowrap; font-family: inherit; }
button.rk-st { cursor: pointer; }
.rk-st.ok { background: #E3F4E8; color: #2E7D46; }
.rk-st.bad { background: #FCEBEC; color: var(--primary-color); }
.rk-st.done { background: var(--dark-gray); color: #fff; }
.rk-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 3px; font-size: 12px; color: var(--font-mid-gray); }
.rk-done { font-size: 12.5px; line-height: 1.45; color: var(--font-dark-gray); background: var(--light-gray); border-radius: 8px; padding: 7px 10px; overflow-wrap: anywhere; white-space: pre-line; }
.rk-done b { color: var(--dark-gray); font-weight: 700; }
.rk-chip { display: inline-flex; align-items: center; background: var(--light-gray); color: var(--font-dark-gray); border-radius: 10px; padding: 2px 8px; font-size: 11.5px; font-weight: 600; }
.rk-ms { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--font-mid-gray); }
.rk-dots { display: flex; gap: 4px; flex: 1; }
.rk-dot { flex: 1; height: 7px; border-radius: 4px; background: var(--mid-gray); }
.rk-dot.d { background: #2E7D46; }
.rk-dot.late { background: var(--primary-color); }
.rk-foot { display: flex; align-items: center; gap: 10px; }
.rk-next { flex: 1; min-width: 0; font-size: 12.5px; }
/* Same yellow as the Scorecard's Issue button */
.rk-issue { flex-shrink: 0; background: #FBF0DA; border: 1px solid #F0DDB4; color: #9A6B15; border-radius: 8px; padding: 5px 12px; font-size: 12px; font-weight: 600; font-family: inherit; cursor: pointer; }
.rk-issue:hover { border-color: #9A6B15; }
.rk-next.late { color: var(--primary-color); font-weight: 600; }
.rk-next.ok { color: #2E7D46; }
.rk-next-link { display: flex; align-items: center; gap: 5px; width: fit-content; max-width: 100%; margin-top: 3px; font-size: 12px; font-weight: 400; color: var(--font-mid-gray); }
.rk-next-link span { text-decoration: underline; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.rk-next-link:hover { color: var(--primary-color); }
.rk-next-link svg { width: 11px; height: 11px; fill: currentColor; flex-shrink: 0; }
.rk-empty { display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center; background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; padding: 40px 24px; }
.rk-empty svg { width: 28px; height: 28px; fill: var(--font-mid-gray); flex-shrink: 0; }

@media(max-width: 767px) {
    .rk-rocks { grid-template-columns: 1fr; }
    .rk-sum { gap: 14px; }
}
</style>
